import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { MatterType } from "../models/MatterType";
import { MatterTypeService } from "../services/MatterTypeService";
import { useAuth } from "./AuthContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { IntakeQuestion } from "../models/IntakeQuestion";
import { EventSubtype } from "../models/EventSubtype";
import { EventType } from "../models/EventType";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface MatterTypeContextProps {
  matterTypes: MatterType[];
  selectedMatterType: MatterType | null;
  setSelectedMatterType: (matterTypeId: string) => void;
  questions: IntakeQuestion[];
  eventTypes: EventType[];
  eventSubtypes: EventSubtype[];
  createMatterType: (
    matterTypeData: Omit<
      MatterType,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter"
    >,
  ) => Promise<string>;
  updateMatterType: (
    matterTypeId: string,
    file: globalThis.File | null,
    updatedData: Partial<Omit<MatterType, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteMatterTypeById: (matterTypeId: string) => Promise<void>;
  deleteEventType: (matterTypeId: string, eventTypeId: string) => Promise<void>;
  deleteEventSubtype: (
    matterTypeId: string,
    eventSubtypeId: string,
  ) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const MatterTypeContext = createContext<MatterTypeContextProps | undefined>(
  undefined,
);

export const useMatterType = (): MatterTypeContextProps => {
  const context = useContext(MatterTypeContext);
  if (!context) {
    throw new Error("useMatterType must be used within a MatterTypeProvider");
  }
  return context;
};

export const MatterTypeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();

  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [selectedMatterType, setSelectedMatterType] =
    useState<MatterType | null>(null);
  const [questions, setQuestions] = useState<IntakeQuestion[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [eventSubtypes, setEventSubtypes] = useState<EventSubtype[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  const loadMatterTypes = async () => {
    try {
      const all = await MatterTypeService.getAllMatterTypes();
      setMatterTypes(all);
      setErrors((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        list: new Error(err.message),
      }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    if (!user) return;
    setLoadings((prev) => ({ ...prev, list: true }));
    loadMatterTypes();
  }, [user]);

  useSocketEventByIds("mattertypes:changed", ["*"], async (updatedDocument) => {
    if (updatedDocument._id === selectedMatterType?._id) {
      if (
        selectedMatterType?.questionTaskStatus !== "processing" &&
        updatedDocument.questionTaskStatus === "completed"
      ) {
        const questions = await MatterTypeService.getMatterTypeQuestions(
          updatedDocument._id,
        );
        setQuestions(questions ?? []);
      }
      if (
        selectedMatterType?.eventTypeTaskStatus !== "processing" &&
        updatedDocument.eventTypeTaskStatus === "completed"
      ) {
        const eventTypes = await MatterTypeService.getEventTypes(
          updatedDocument._id,
        );
        setEventTypes(eventTypes ?? []);
      }
      setSelectedMatterType(updatedDocument);
    } else {
      loadMatterTypes();
    }
  });

  useSocketEventByIds(
    "eventtypes:changed",
    ["*"],
    async (updatedDocument: EventType) => {
      if (selectedMatterType?._id === updatedDocument.matterType) {
        setEventTypes((prev) => [...prev, updatedDocument]);
      }
    },
  );

  useSocketEventByIds(
    "eventsubtypes:changed",
    ["*"],
    async (updatedDocument: EventSubtype) => {
      if (selectedMatterType?._id === updatedDocument.matterType) {
        setEventSubtypes((prev) => [...prev, updatedDocument]);
      }
    },
  );

  const setSelectedMatterTypeById = async (matterTypeId: string) => {
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const data = await MatterTypeService.getMatterType(matterTypeId);
      setSelectedMatterType(data);

      const questions =
        await MatterTypeService.getMatterTypeQuestions(matterTypeId);
      setQuestions(questions ?? []);
      const eventTypes = await MatterTypeService.getEventTypes(matterTypeId);
      const eventSubtypes =
        await MatterTypeService.getEventSubtypes(matterTypeId);
      setEventTypes(eventTypes ?? []);
      setEventSubtypes(eventSubtypes ?? []);

      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      setSelectedMatterType(null);
      setErrors((prev) => ({
        ...prev,
        details: new Error(err.message),
      }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createMatterType = async (
    matterTypeData: Omit<
      MatterType,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter"
    >,
  ): Promise<string> => {
    const id = await MatterTypeService.createMatterType({
      ...matterTypeData,
      createdBy: user!.uid,
    });
    await loadMatterTypes();
    return id;
  };

  const updateMatterType = async (
    matterTypeId: string,
    file: globalThis.File | null,
    updatedData: Partial<Omit<MatterType, "_id" | "createdAt" | "updatedAt">>,
  ): Promise<void> => {
    await MatterTypeService.updateMatterType(matterTypeId, file, updatedData);
    await setSelectedMatterTypeById(matterTypeId);
    await loadMatterTypes();
  };

  const deleteMatterTypeById = async (matterTypeId: string): Promise<void> => {
    await MatterTypeService.deleteMatterType(matterTypeId);
    setMatterTypes((prev) =>
      prev.filter((matterType) => matterType._id !== matterTypeId),
    );
  };

  const deleteEventType = async (
    matterTypeId: string,
    eventTypeId: string,
  ): Promise<void> => {
    await MatterTypeService.deleteEventType(matterTypeId, eventTypeId);
  };

  const deleteEventSubtype = async (
    matterTypeId: string,
    eventSubtypeId: string,
  ): Promise<void> => {
    await MatterTypeService.deleteEventSubtype(matterTypeId, eventSubtypeId);
  };

  return (
    <MatterTypeContext.Provider
      value={{
        matterTypes,
        selectedMatterType,
        questions,
        eventTypes,
        eventSubtypes,
        loading: loadings,
        error: errors,
        setSelectedMatterType: setSelectedMatterTypeById,
        createMatterType,
        updateMatterType,
        deleteMatterTypeById,
        deleteEventType,
        deleteEventSubtype,
      }}
    >
      {children}
    </MatterTypeContext.Provider>
  );
};
